import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { CONTACT_WHATSAPP_URL } from '../../config/brand';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { cardTheme } from '../../styles/cardTheme';

type FaqItem = { q: string; a: string };

function formatTeacherNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} و ${names[1]}`;
  return `${names.slice(0, -1).join('، ')}، و ${names[names.length - 1]}`;
}

const HomeFaq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [teacherNames, setTeacherNames] = useState<string[]>([]);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get<{
          success: boolean;
          data?: Array<{ fullName: string; active: boolean }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data) {
          setTeacherNames(
            response.data.data
              .filter((t) => t.active && t.fullName?.trim())
              .map((t) => t.fullName.trim())
          );
        }
      } catch (error) {
        console.error('Error fetching teachers for FAQ:', error);
      }
    };

    fetchTeachers();
  }, []);

  const faqs: FaqItem[] = useMemo(() => {
    const namesText = formatTeacherNames(teacherNames);
    const teachersAnswer = namesText
      ? `المدرسين الموجودين حالياً على المنصة: ${namesText}. تقدر تشوف كورسات كل مدرس من قسم المدرسين في الصفحة الرئيسية.`
      : 'تقدر تشوف قائمة المدرسين وكورساتهم من قسم المدرسين في الصفحة الرئيسية.';

    return [
      {
        q: 'مين المدرسين اللي موجودين على المنصة؟',
        a: teachersAnswer,
      },
      {
        q: 'المنصة مجانية ولا مدفوعة؟',
        a: 'التسجيل على المنصة مجاني، والكورسات بعضها مجاني وبعضها باشتراك مدفوع — والسعر بيظهر على صفحة كل كورس قبل ما تشترك.',
      },
      {
        q: 'لو نسيت كلمة السر أعمل إيه؟',
        a: 'من صفحة تسجيل الدخول اختار «نسيت كلمة السر»، أدخل رقم هاتفك أو بريدك، وهتوصلك خطوات إعادة تعيين كلمة المرور.',
      },
      {
        q: 'إزاي أتواصل مع الدعم الفني؟',
        a: 'تقدر تستخدم زر الدعم الفني في الصفحة، أو تتواصل عبر واتساب أو تليجرام — وفريق الدعم بيرد في أقرب وقت خلال ساعات العمل.',
      },
    ];
  }, [teacherNames]);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      id="faq-section"
      className="relative scroll-mt-24 overflow-hidden py-16 sm:py-20 lg:py-24"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-green-50/60 via-transparent to-gold-50/40"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-2xl md:mb-10">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-gold-500" aria-hidden />
            <p className="text-sm font-semibold text-gold-700">مساعدة سريعة</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${cardTheme.iconBoxGold}`}>
              <HelpCircle className="h-5 w-5" aria-hidden />
            </span>
            <h2 className={`text-[clamp(1.75rem,3.5vw,3rem)] font-bold font-thmanyah ${cardTheme.title}`}>
              أسئلة شائعة
            </h2>
          </div>
          <p className={`mt-3 text-sm leading-relaxed sm:text-base ${cardTheme.subtitle}`}>
            إجابات مختصرة لأكثر الأسئلة اللي بيسألها طلاب الثانوية الأزهرية وأولياء الأمور.
          </p>
        </div>

        <ul className="mx-auto flex max-w-3xl flex-col gap-3 sm:gap-4">
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;
            const num = String(index + 1).padStart(2, '0');
            const panelId = `faq-panel-${index}`;
            const buttonId = `faq-button-${index}`;

            return (
              <li
                key={item.q}
                className={`${cardTheme.shell} ${cardTheme.shellHover} ${
                  isOpen ? 'border-gold-400/60 shadow-lg shadow-green-900/10' : ''
                }`}
              >
                <button
                  id={buttonId}
                  type="button"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="flex w-full min-h-12 items-center gap-3 p-3.5 text-right sm:gap-4 sm:p-4"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold tabular-nums transition-colors ${
                      isOpen
                        ? 'bg-gold-500 text-green-950'
                        : 'bg-green-50 text-green-800 border border-green-100'
                    }`}
                  >
                    {num}
                  </span>
                  <span
                    className={`min-w-0 flex-1 text-sm font-semibold leading-relaxed sm:text-base ${
                      isOpen ? 'text-green-900' : 'text-green-800'
                    }`}
                  >
                    {item.q}
                  </span>
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                      isOpen
                        ? 'rotate-180 border-gold-300 bg-gold-50 text-gold-700'
                        : 'border-green-100 bg-white text-green-700'
                    }`}
                    aria-hidden
                  >
                    <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                </button>

                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-green-50 px-3.5 pb-4 pe-3.5 ps-[3.75rem] sm:px-4 sm:pb-5 sm:ps-[4.25rem]">
                      <div className={`mb-3 h-0.5 w-10 ${cardTheme.divider}`} />
                      <p className="text-sm leading-relaxed text-gray-600 sm:text-base sm:leading-7">
                        {item.a}
                        {index === 3 && (
                          <>
                            {' '}
                            <a
                              href={CONTACT_WHATSAPP_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-semibold text-green-800 underline decoration-gold-400/70 underline-offset-2 hover:text-gold-700"
                            >
                              <MessageCircle className="inline h-3.5 w-3.5" aria-hidden />
                              تواصل واتساب
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default HomeFaq;
